import React, { useState } from 'react';
import UserService from '../services/userService';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { loggedUser, loggedService } from '../store/userSlice';
import { loggOutGuest } from '../store/guestSlice';

import LoadindForButton from '../components/LoadingForButton';
import Cookies from 'js-cookie';
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import { FaEnvelope, FaLock } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Footer from '../components/Footer';
import AuthSideVisual from '../components/AuthSideVisual';
import ServerStatusNotice from '../components/ServerStatusNotice';

const Login = () => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    const [errors, setErrors] = useState({});
    const [invalidCredentials, setInvalidCredentials] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const serverStatus = useSelector((state) => state.serverStore.status);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setErrors({ ...errors, [name]: '' });
        setFormData({
            ...formData,
            [name]: value,
        });
        setInvalidCredentials("");
    };

    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.email) {
            newErrors.email = t('emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('invalidEmailFormat');
        }
        if (!formData.password) {
            newErrors.password = t('passwordRequired');
        } else if (formData.password.length < 8) {
            newErrors.password = t('passwordTooShort');
        } else if (formData.password.length > 50) {
            newErrors.password = t('maxLengthError', { count: 50 });
        }
        return newErrors;
    };

    const handleSubmit = (e) => {
        setLoading(true);
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setLoading(false);
            return;
        }

        UserService.loginUser({ ...formData })
            .then((res) => {
                Cookies.remove('gAuthToken');
                Cookies.set('authToken', res.data.token, { path: '/' });
                dispatch(loggedUser(res.data.user));
                dispatch(loggedService(res.data.service || null));

                if (res.data.user.role === 'admin') {
                    setTimeout(() => navigate('/organization'), 1000);
                    dispatch(loggOutGuest());
                } else {
                    setTimeout(() => navigate('/usluge'), 2000);
                }
                setTimeout(() => setLoading(false), 1000);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    setInvalidCredentials(t('loginWrongCredentials'));
                } else {
                    setInvalidCredentials(t('loginErrorGeneric'));
                }
                setLoading(false);
            });
    };

    return (
        <>
            <Helmet>
                <title>{t('meta.login.title')}</title>
                <meta name="description" content={t('meta.login.description')} />
            </Helmet>
            <div className='min-h-screen bg-gradient-to-br from-cyan-200 via-white to-pink-300 flex flex-col'>
                <main className='flex-1'>
                    <div className='flex justify-between items-center px-4 py-4 lg:px-10 relative'>
                        <Link to="/" className="flex items-center gap-2 shrink-0 no-underline">
                            <img src="/logo2.png" className="w-8 h-8 md:w-9 md:h-9" alt="Weddinfy" />
                            <span className="font-display text-xl md:text-[22px] font-semibold text-stone-900">Weddinfy</span>
                        </Link>
                    </div>
                    <div className="lg:py-10 lg:px-10 flex flex-col lg:flex-row items-center h-full">
                        <div className="w-full lg:w-1/2 max-w-screen-md mx-auto">
                            <form onSubmit={handleSubmit} className="p-4 pt-10 lg:pt-0 w-full">
                                <div className='flex flex-col items-center mb-7'>
                                    <img src="/logo2.png" className="w-12 h-12 lg:w-32 lg:h-32" alt="Weddinfy" />
                                    <h2 className="text-3xl font-bold my-2 mx-2">{t('welcomeBack')}</h2>
                                    <span className='block mx-2 text-gray-700'>{t('loginSubtitle')}</span>
                                </div>

                                <div className="relative">
                                    <label>{t('email')}</label>
                                    <FaEnvelope className='inputWeddingIcon' />
                                    <input
                                        type="email"
                                        name="email"
                                        maxLength={50}
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={`inputWedding ${errors.email ? '!border-red-500' : 'inputFieldNormal'}`}
                                    />
                                </div>
                                {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}

                                <div className="relative mt-4">
                                    <label>{t('password')}</label>
                                    <FaLock className='inputWeddingIcon' />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        maxLength={50}
                                        value={formData.password}
                                        onChange={handleChange}
                                        className={`inputWedding ${errors.password ? '!border-red-500' : 'inputFieldNormal'}`}
                                    />
                                    <button type="button" onClick={toggleShowPassword} className="absolute bottom-3 right-4">
                                        {showPassword ? <IoEyeOutline size={20} /> : <IoEyeOffOutline size={20} />}
                                    </button>
                                </div>
                                {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}

                                <Link to="/forgot-password" className='flex justify-end text-lg font-semibold my-5 underline'>
                                    {t('forgotPassword')}
                                </Link>

                                {invalidCredentials && (
                                    <p className="bg-red-100 rounded-lg p-2 text-red-700 text-center my-4">
                                        {invalidCredentials}
                                    </p>
                                )}

                                <ServerStatusNotice />

                                <button type="submit" className="submitButton w-full" disabled={loading || serverStatus !== 'ready'}>
                                    {loading || serverStatus === 'checking' ? <LoadindForButton /> : t('loginButton')}
                                </button>

                            </form>
                            <div className="pt-10 text-center mb-10">
                                <span className="text-gray-700">{t('dontHaveAccount')} </span>
                                <Link to="/register" className="text-gray-800 font-semibold underline">
                                    {t('signUp')}
                                </Link>
                            </div>
                        </div>

                        {/* Right side — AuthSideVisual instead of static image */}
                        <div className="hidden lg:flex w-1/2 flex-col items-center justify-center py-8 px-4">
                            <AuthSideVisual />
                            <p className="text-gray-600 text-sm max-w-xs text-center mt-6 leading-relaxed">{t('slogan')}</p>
                        </div>
                    </div>
                </main>
                <footer>
                    <Footer />
                </footer>
            </div>
        </>
    );
};

export default Login;
